import { useMutation, useQueryCache } from "react-query";
import useNetworkHeaders from "../../../hooks/useNetworkHeaders";
import { putNotifySubscriptionPreferences } from "../../../queries/notify";
import { buildNotifyInfiniteSubscriberContactMethodsQueryKey } from "./useInfiniteNotifySubscriberContactMethods";
import { buildNotifySubscriberContactMethodQueryKey } from "./useNotifySubscriberContactMethod";
import { buildNotifyContactsQueryKey } from "./useInfiniteNotifyContacts";
import { buildNotifySubscriberContactMethodsQueryKey } from "./useNotifySubscriberContactMethodsForLoggedInUser";

interface IMutationInput {
  subscriberContactMethodIds: number[];
  subscriptionGroupIds: number[];
  type: "Subscribe" | "Unsubscribe";
  overwriteExistingPreference?: boolean;
}

interface IUseBulkNotifySubscriptionPreferencesUpdate {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

export function useBulkNotifySubscriptionPreferencesUpdate({
  onSuccess,
  onError,
}: IUseBulkNotifySubscriptionPreferencesUpdate = {}) {
  const networkHeaders = useNetworkHeaders();
  const queryCache = useQueryCache();

  const [executeBulkUpdate, { isLoading }] = useMutation(
    async (mutations: IMutationInput[]) => {
      // Execute all mutations in parallel - will throw if any fail
      const results = await Promise.all(
        mutations.map((mutation) => putNotifySubscriptionPreferences(networkHeaders)(mutation)),
      );

      return results;
    },
    {
      onSuccess: async (results) => {
        // Invalidate queries once after all mutations succeed
        await queryCache.invalidateQueries(buildNotifyInfiniteSubscriberContactMethodsQueryKey());
        await queryCache.invalidateQueries(buildNotifyContactsQueryKey());
        await queryCache.invalidateQueries(buildNotifySubscriberContactMethodsQueryKey());

        // have to invalidate quereies for each individual contact method
        const allContactMethodIds = new Set<number>();
        results.forEach((resultArray) => {
          resultArray.forEach((item: { subscriberContactMethodId: number }) => {
            allContactMethodIds.add(item.subscriberContactMethodId);
          });
        });

        await Promise.all(
          Array.from(allContactMethodIds).map((id) =>
            queryCache.invalidateQueries(buildNotifySubscriberContactMethodQueryKey({ id })),
          ),
        );

        if (onSuccess) {
          onSuccess();
        }
      },
      onError: (error: unknown) => {
        if (onError) {
          onError(error);
        }
      },
    },
  );

  return { executeBulkUpdate, isLoading };
}
